import React, { useRef, useState } from 'react';
import { Col, Row, Spin, Typography } from 'antd';
import { LinkedInManager, OrganizationMemberManager } from '@copilot/data';
import { useEffectAsync, useFetch } from '@copilot/common/hooks/common';
import { LinkedInMetaActions } from '@copilot/common/store/actions/linkedInMeta';
import { Config } from '@copilot/common/config';
import LinkedinLoginResult, {
	LinkedInResultStatus,
} from '@copilot/common/components/linkedin/login/result';
import { SignalRPeer } from '@copilot/common/screensharing/client';
import Peer from 'simple-peer';
import { isEmpty, isUndefined } from 'lodash';

interface LinkedInSettingsProps {
	user: { id: string };
	isLoggedIn: boolean;
}

const LinkedInSettings: React.FC<LinkedInSettingsProps> = (props) => {
	const { user, isLoggedIn } = props;
	const [isLoggedInFetch, fetchIsLoggedIn] = useFetch(
		(id: string) =>
			OrganizationMemberManager.checkIsLoggedIn(id).then((r) => ({
				orgMemberId: user.id,
				isLoggedIn: r,
			})),
		LinkedInMetaActions.loadLinkedInMeta
	);
	useEffectAsync(async () => {
		await fetchIsLoggedIn(user.id);
	}, [user.id]);

	const partnerVideo = useRef<HTMLVideoElement>(null);
	const [loginRoomName, fetchLoginRoomName] = useFetch(LinkedInManager.getLoginRoomName);
	const [rtcState, setRtcState] =
		useState<'complete' | 'timeout' | 'running' | 'preparing'>('preparing');
	const [isRtcLoginVisible, setIsRtcLoginVisible] = useState<boolean>(false);

	useEffectAsync(async () => {
		const roomName = loginRoomName.data;
		const shouldInitialize = isRtcLoginVisible && roomName && rtcState === 'preparing';
		if (!shouldInitialize) return;
		const options: Peer.Options = {
			config: {
				iceServers: [
					{
						urls: 'stun:turn.copilotai.com:80',
					},
					{
						urls: 'stun:turn.copilotai.com:3478',
					},
					{
						urls: 'turn:turn.copilotai.com:80',
						credential: 'fKXq66HHa2W2aRy',
						username: 'copilotai',
					},
					{
						urls: 'turns:turn.copilotai.com:443',
						credential: 'fKXq66HHa2W2aRy',
						username: 'copilotai',
					},
					{
						urls: 'turns:turn.copilotai.com:443?transport=tcp',
						credential: 'fKXq66HHa2W2aRy',
						username: 'copilotai',
					},
					{
						urls: 'turns:turn.copilotai.com:3478',
						credential: 'fKXq66HHa2W2aRy',
						username: 'copilotai',
					},
				],
			},
		};
		const handleComplete = (isTimeout: boolean) => {
			setRtcState(isTimeout ? 'timeout' : 'complete');
		};
		const signalRPeer = new SignalRPeer(
			`${Config.webRtcSignalingURL}/hubs/rtcsignaling`,
			options,
			handleComplete
		);
		signalRPeer.on('connect', () => {
			console.log('Connected!');
			setRtcState('running');
			partnerVideo.current?.addEventListener('mouseup', mouseupListener);
			window.document.addEventListener('paste', pasteListener);
			window.addEventListener('keydown', keydownListener);
		});
		signalRPeer.on('close', () => {
			partnerVideo.current?.removeEventListener('mouseup', mouseupListener);
			window.document.removeEventListener('paste', pasteListener);
			window.removeEventListener('keydown', keydownListener);
		});
		signalRPeer.on('stream', (stream: any) => {
			console.log('got the stream');
			if (partnerVideo.current) {
				console.log('adding stream');
				partnerVideo.current.srcObject = stream;
				partnerVideo.current.play();
			}
		});
		await signalRPeer.initConn();
		signalRPeer.joinConnection(roomName);

		const mouseupListener = (e: MouseEvent) => {
			const data = getMouseData(e);
			data.click = true;
			console.log('send mouseup', data);
			signalRPeer.send(data);
		};

		const isKeyboardPasteEvent = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && (e.code === 'KeyV')) {
				return true;
			}

			return false;
		};
		const keydownListener = (e: KeyboardEvent) => {
			if (isKeyboardPasteEvent(e)) return;
			e.preventDefault();

			const data = {
				code: e.code,
				shift: e.shiftKey,
				meta: e.metaKey,
				control: e.ctrlKey,
				alt: e.altKey,
			};

			signalRPeer.send(data);
		};
		
		const pasteListener = (event: ClipboardEvent) => {
			const clipboardData = event.clipboardData?.getData('text');

			if (isUndefined(clipboardData) || isEmpty(clipboardData)) return;

			signalRPeer.send({ clipboardData });
		};

		const getMouseData = (e: MouseEvent) => {
			const data: any = {
				clientX: 0,
				clientY: 0,
				canvasWidth: 0,
				canvasHeight: 0,
			};
			const videoSize = partnerVideo.current?.getBoundingClientRect() ?? {
				x: 0,
				y: 0,
				width: 0,
				height: 0,
			};

			data.clientX = e.clientX - videoSize.x;
			data.clientY = e.clientY - videoSize.y;

			if (partnerVideo.current) {
				data.canvasWidth = videoSize.width;
				data.canvasHeight = videoSize.height;
			}

			return data;
		};
	}, [isRtcLoginVisible, loginRoomName.data, rtcState]);

	const showLoginRoom = () => {
		setIsRtcLoginVisible(true);
		fetchLoginRoomName(user.id);
	};

	return (
		<>
			<Row>
				<Col span={24}>
					{!isLoggedInFetch.isFetching && (
						<Row>
							{!isRtcLoginVisible && (
								<Col span={24}>
									{isLoggedIn ? (
										<LinkedinLoginResult
											callToAction="Service running fine. No actions needed."
											header="Your LinkedIn is successfully connected"
											status={LinkedInResultStatus.Success}
											onClickCallback={showLoginRoom}
											buttonText="Reconnect"
										/>
									) : (
										<LinkedinLoginResult
											callToAction="You will need to connect a LinkedIn account to use the platform."
											header="Please reconnect your LinkedIn account"
											status={LinkedInResultStatus.Error}
											onClickCallback={showLoginRoom}
											buttonText="Reconnect"
										/>
									)}
								</Col>
							)}
							{isRtcLoginVisible && rtcState === 'preparing' && (
								<Col span={24} style={{ textAlign: 'center' }}>
									{loginRoomName.isFetching ||
										(!loginRoomName.error && (
											<>
												<Typography.Title level={2}>
													Loading. Please wait...
												</Typography.Title>
												<Spin />
											</>
										))}
									{!loginRoomName.isFetching && loginRoomName.error && (
										<LinkedinLoginResult
											header="Sorry an error has occurred. Please try again."
											status={LinkedInResultStatus.Error}
											buttonText="Try again"
											onClickCallback={showLoginRoom}
										/>
									)}
								</Col>
							)}
							<Col
								span={24}
								style={{
									textAlign: 'center',
									display:
										isRtcLoginVisible && rtcState === 'running'
											? 'block'
											: 'none',
								}}
							>
								<video
									width={800}
									height={600}
									playsInline
									autoPlay
									className="video"
									ref={partnerVideo}
								>
									Please wait while we connect you to LinkedIn
								</video>
							</Col>
							{isRtcLoginVisible && rtcState === 'complete' && (
								<Col span={24}>
									<LinkedinLoginResult
										header="Your LinkedIn is successfully connected"
										status={LinkedInResultStatus.Success}
									/>
								</Col>
							)}
							{isRtcLoginVisible && rtcState === 'timeout' && (
								<Col span={24}>
									<LinkedinLoginResult
										header="We've exceeded our time limit, please refresh and try again"
										status={LinkedInResultStatus.Error}
									/>
								</Col>
							)}
						</Row>
					)}
					{isLoggedInFetch.isFetching && (
						<div style={{ textAlign: 'center' }}>
							<Spin />
						</div>
					)}
				</Col>
			</Row>
		</>
	);
};

export default LinkedInSettings;
